/* S15: pill タブ切替（凍結モック L2601-2633 のタブ挙動の移植・おすすめ/ランキング共用）。
 *
 * モックからの継承: click でパネル即時切替（アニメなし）・active クラス切替・
 * 2 階層（おすすめ）は親タブを切り替えても各グループのサブタブ選択状態を独立保持。
 *
 * モックとの乖離（S15 ADR）:
 * - モックの document 全体委譲（display インライン切替）は同種セクション複数設置で
 *   相互誤爆する DOM アーティファクト → 同一 .shopify-section 内解決に置換
 *   （旧 tol-scrollbar.js〔現 tol-slider.js・0713-#32 で置換〕と同型判断）。
 * - パネル切替はインライン display でなく hidden 属性（保険 CSS = tol-pill-tabs.css）。
 * - a11y はモック非実装 → WAI-ARIA APG Tabs 準拠を追加（G1 M-1 裁定）:
 *   role=tablist/tab/tabpanel + aria-selected はサーバーレンダ（セクション Liquid）、
 *   本 JS が左右矢印 + Home/End の roving tabindex と activate を持つ。
 *   activation は automatic（矢印フォーカス移動で即切替・パネルは軽量な静的グリッド）。
 *
 * マークアップ契約（セクション側）:
 *   <div data-tol-tabs="<group>" role="tablist"> <button data-tol-tab="<key>" role="tab"> …
 *   <div data-tol-panels="<group>"> <div data-tol-panel="<key>" role="tabpanel" class="tol-tabs__panel"> …
 *   - group は同一セクション内で一意（解決は同 .shopify-section 内）。
 *   - 入れ子可: 親グループのパネル内に別グループの tablist/panels を置ける
 *     （パネル列挙は「closest が自分の panels コンテナ」のものに限定して衝突回避）。
 *   - 初期状態（is-active / aria-selected / tabindex / hidden）はサーバーレンダが正。
 *
 * custom element は作らない（tol-hero.js / tol-slider.js のアロー IIFE +
 * data-*-init ガード + AbortController teardown 前例を踏襲）。Dawn 本体無改修・tol- 隔離。
 */
(() => {
  /* 同一 src の <script> はセクション複数配置で複数回実行される（フェッチは1回・実行はタグごと） */
  if (window.__tolTabsLoaded) return;
  window.__tolTabsLoaded = true;

  const init = (root) => {
    root.querySelectorAll('[data-tol-tabs]').forEach((list) => {
      if (list.dataset.tolTabsInit) return;
      const scope = list.closest('.shopify-section') || document;
      const group = list.dataset.tolTabs;
      const panelsRoot = scope.querySelector('[data-tol-panels="' + CSS.escape(group) + '"]');
      if (!panelsRoot) return;
      list.dataset.tolTabsInit = 'true';

      const abort = new AbortController();
      const signal = abort.signal;
      /* activate は下で定義後に代入する（shopify:block:select から呼ぶため公開する） */
      list._tolTabs = { abort };

      const tabs = Array.from(list.querySelectorAll('[data-tol-tab]'));
      /* 入れ子対策: 自分の panels コンテナ直属のパネルのみ（サブグループのパネルを除外） */
      const panels = Array.from(panelsRoot.querySelectorAll('[data-tol-panel]')).filter(
        (p) => p.closest('[data-tol-panels]') === panelsRoot
      );

      const activate = (tab) => {
        const key = tab.dataset.tolTab;
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => {
          const wasHidden = p.hidden;
          p.hidden = p.dataset.tolPanel !== key;
          /* 0713-G5: hidden→表示 に遷移したパネル内のスライダーのみ先頭リセット＋再計算
             （原本仕様「タブ切替時」＝同一タブ再クリック・アクティブタブへの矢印往復では
             横位置を保持）。hidden=false 代入後の同期 dispatch なので reset 内の layout read は
             最新値を読む（hidden 中は display:none で clientWidth 0）。
             [data-tol-slider] 不在パネル（tol-recommend / tol-product-tabs）は空 = no-op。 */
          if (!p.hidden && wasHidden) {
            p.querySelectorAll('[data-tol-slider]').forEach((w) => {
              w.dispatchEvent(new CustomEvent('tol:slider:reset'));
            });
            /* 0713-G9（0713-#12）: 外部アプリ（Glood）の受け皿パネルの再計測。
               実測（2026-07-30 DEV）: Glood のカルーセル（Glide.js）は hidden（display:none）中に
               幅 0 で初期化され、表示に切り替えても【自動では回復しない】（スライド幅 27px のまま）。
               window の resize を 1 回投げると完全に回復する（27px → 318px・scrollWidth 974 → 2624）。
               ★初回表示の 1 回だけに絞る（data-tol-recommend-measured で一度きり）。
                 必要なのは「幅 0 で初期化されたものの復旧」であって毎回の再計算ではないため、
                 ユーザー操作ごとにグローバルイベントを撒かない（実 diff レビュー HIGH-3）。
               ★これは一時措置。Glood は公開メソッドを持たず（window.glood はデータのみ・
                 appEmbedSettings['init-event-name'] も既定 ""）、公式の再計算 API が無いため。
                 ベンダーから正式なフック契約を得られたらそれへ差し替える。
               ★resize は window 全体に届く（永続的な window resize リスナを持つ全コードへ）。
                 属性ガードは dispatch する条件でしかなく受信側は絞れない。実測では TOP の
                 tol-slider 10 インスタンスすべてが 2 回の resize 後も完全同一状態（refresh は
                 scrollLeft を書かない冪等処理・--pc-static の CSS ガードとも直交）。 */
            if (p.dataset.tolRecommendSlot && !p.dataset.tolRecommendMeasured) {
              p.dataset.tolRecommendMeasured = 'true';
              window.dispatchEvent(new Event('resize'));
            }
          }
        });
      };

      /* エディタの shopify:block:select から該当パネルを開かせるために公開（下の listener が使う） */
      list._tolTabs.activate = activate;
      list._tolTabs.tabs = tabs;
      list._tolTabs.panels = panels;

      list.addEventListener(
        'click',
        (e) => {
          const tab = e.target.closest('[data-tol-tab]');
          if (tab && tabs.includes(tab)) activate(tab);
        },
        { signal }
      );

      list.addEventListener(
        'keydown',
        (e) => {
          const current = e.target.closest('[data-tol-tab]');
          if (!current || !tabs.includes(current)) return;
          const i = tabs.indexOf(current);
          let next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (e.key === 'Home') next = tabs[0];
          else if (e.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          e.preventDefault();
          activate(next);
          next.focus();
        },
        { signal }
      );
    });
  };

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* エディタでブロックを選択 → そのブロックを含むパネルのタブへ切り替える（tol-hero.js:269 と同型）。
     公式要件「選択されたセクション/ブロックは選択中ずっと可視であること」への対応:
     https://shopify.dev/docs/storefronts/themes/best-practices/editor/integrate-sections-and-blocks
     hidden なパネルに入った app block（0713-G9 のタブ 2〜4）を選んでも見えないままになるのを防ぐ。 */
  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target.closest && e.target.closest('[data-tol-panel]');
    if (!panel) return;
    const panelsRoot = panel.closest('[data-tol-panels]');
    if (!panelsRoot) return;
    const scope = panelsRoot.closest('.shopify-section') || document;
    const list = scope.querySelector('[data-tol-tabs="' + CSS.escape(panelsRoot.dataset.tolPanels) + '"]');
    const api = list && list._tolTabs;
    if (!api || !api.activate) return;
    const tab = api.tabs.find((t) => t.dataset.tolTab === panel.dataset.tolPanel);
    if (tab) api.activate(tab);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-tol-tabs]').forEach((list) => {
      if (list._tolTabs) list._tolTabs.abort.abort();
    });
  });
})();
